import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import {
  Plus,
  ShoppingCart,
  Download,
  Printer,
  Trash2,
  Calendar,
  FileText,
  Save,
  ArrowLeft,
  Package,
  Layers,
  ScanBarcode,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { ReactSelect } from "@/components/ui/react-select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import AddSerialsModal from "../inventory/components/AddSerialsModal";

const NewGRN = () => {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const store_id = useSelector((state) => state.current_store?.id || null);
  const employee_id =
    useSelector((state) => state.user_data).employee?.id || null;

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";



  const currentUserEmployee = useSelector((state) => state.user_data)?.employee;

  const [grn, setGrn] = useState({
    purchase_order_id: "",
    received_by: employee_id, // Default to current user
    received_date: new Date().toISOString().split("T")[0],
    status: "pending",
    reference: "",
    document: "",
    document_type: "Invoice",
    document_status: "Original",
    document_url: "",
    payment_method: "",
    notes: "",
    sub_total_amount: 0,
    total_tax: 0,
    total_amount: 0,
    meta: [],
  });

  const [grnItems, setGrnItems] = useState([]);
  const [employeeSearchInput, setEmployeeSearchInput] = useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeLabels, setEmployeeLabels] = useState({});
  const [poSearchInput, setPoSearchInput] = useState("");
  const [poSearchTerm, setPoSearchTerm] = useState("");

  const [productSearchInput, setProductSearchInput] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productLabels, setProductLabels] = useState({});

  const [configModal, setConfigModal] = useState({
    isOpen: false,
    itemIndex: null,
  });

  // Serial Modal State
  const [serialModal, setSerialModal] = useState({
    isOpen: false,
    itemIndex: null,
    productName: "",
    quantity: 0,
    initialSerials: [],
  });

  const openSerialModal = (index) => {
    const item = grnItems[index];
    const product =
      item.product ||
      orderProducts.find(
        (p) => String(p.product_id) === String(item.product_id),
      )?.product ||
      searchedProductsData.find(
        (p) => String(p.id) === String(item.product_id),
      );

    const remainingToReceive =
      (parseInt(item.order_quantity) || 0) -
      (parseInt(item.already_received_quantity) || 0);

    let qty = parseInt(item.received_quantity);
    if (isNaN(qty) || qty <= 0) {
      if (remainingToReceive > 0) {
        qty = remainingToReceive;
        grnItemChange(index, "received_quantity", remainingToReceive);
      } else {
        toast.error(
          "Please enter a valid received quantity greater than 0 first.",
        );
        return;
      }
    }

    setSerialModal({
      isOpen: true,
      itemIndex: index,
      productName: product?.name || product?.product_name || "Product",
      quantity: qty,
      initialSerials: item.serial_numbers || [],
    });
  };

  const handleSaveSerials = (serials) => {
    grnItemChange(serialModal.itemIndex, "serial_numbers", serials);
  };

  // Memoized list of serials used in other items to prevent duplicates
  const excludeSerials = React.useMemo(() => {
    return grnItems.flatMap((item, idx) =>
      idx === serialModal.itemIndex ? [] : item.serial_numbers || [],
    );
  }, [grnItems, serialModal.itemIndex]);

  // Queries
  const { data: availablePaymentMethods = [], isLoading: paymentMethodsLoading } = useQuery({
    queryKey: ["payment-methods-available", "purchases", store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "purchases",
          store_id: store_id || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
  });
  const { data: ordersData, isFetching: isOrdersFetching } = useQuery({
    queryKey: ["purchaseOrders", poSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PURCHASES.ORDERS, {
        params: {
          store_id,
          with: ["items.product"],
          search: poSearchTerm || undefined,
          per_page: 15,
        },
      });
      return res.data.data;
    },
    enabled:
      hasPermission("purchase order read") &&
      hasPermission("product read") &&
      hasPermission("supplier read"),
  });

  // Merge fetched orders with the currently selected order so the selected
  // value is always present in the options list even after the search changes.
  const orders = React.useMemo(() => {
    const fetched = ordersData || [];
    if (!grn.purchase_order_id) return fetched;
    const alreadyIncluded = fetched.some(
      (o) => String(o.id) === String(grn.purchase_order_id),
    );
    if (alreadyIncluded) return fetched;
    // Try to find the selected order from a previous fetch stored in the query cache
    const cached = queryClient.getQueryData(["purchaseOrders", ""]);
    const selectedOrder = (cached || []).find(
      (o) => String(o.id) === String(grn.purchase_order_id),
    );
    return selectedOrder ? [selectedOrder, ...fetched] : fetched;
  }, [ordersData, grn.purchase_order_id, queryClient]);

  const { data: employees = [], isFetching: isEmployeesFetching } = useQuery({
    queryKey: ["employees", employeeSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES, {
        params: {
          search: employeeSearchTerm || undefined,
          perPage: 15,
          per_page: 15,
        },
      });
      return res.data.data;
    },
    enabled: hasPermission("employee read"),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setEmployeeSearchTerm(employeeSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPoSearchTerm(poSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [poSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProductSearchTerm(productSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchInput]);

  // Server-side product search query - only searches when user enters a search term
  const { data: searchedProductsData = [], isFetching: isProductsFetching } =
    useQuery({
      queryKey: ["inventory-products", productSearchTerm],
      queryFn: async () => {
        const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
          params: {
            search: productSearchTerm,
            per_page: 25,
            perPage: 25,
          },
        });
        return res.data?.data || res.data || [];
      },
      enabled:
        hasPermission("product read") &&
        Boolean(productSearchTerm && productSearchTerm.trim().length > 0),
    });

  useEffect(() => {
    if (currentUserEmployee) {
      setEmployeeLabels((prev) => ({
        ...prev,
        [String(currentUserEmployee.id)]:
          currentUserEmployee.name || currentUserEmployee.full_name,
      }));
    }
  }, [currentUserEmployee]);

  useEffect(() => {
    if (employees.length > 0) {
      setEmployeeLabels((prev) => {
        const next = { ...prev };
        employees.forEach((emp) => {
          next[String(emp.id)] = emp.name || emp.full_name;
        });
        return next;
      });
    }
  }, [employees]);

  // Derive orderProducts from the selected order
  const orderProducts = React.useMemo(() => {
    if (!grn.purchase_order_id) return [];
    const selectedOrder = orders.find(
      (o) => String(o.id) === String(grn.purchase_order_id),
    );
    return selectedOrder?.items || [];
  }, [orders, grn.purchase_order_id]);

  useEffect(() => {
    if (orderProducts.length > 0) {
      setProductLabels((prev) => {
        const next = { ...prev };
        orderProducts.forEach((p) => {
          if (p.product) {
            next[String(p.product_id)] = `${p.product.name || p.product.product_name || ""}${p.product.sku ? ` (${p.product.sku})` : ""}`;
          }
        });
        return next;
      });
    }
  }, [orderProducts]);

  useEffect(() => {
    if (searchedProductsData.length > 0) {
      setProductLabels((prev) => {
        const next = { ...prev };
        searchedProductsData.forEach((p) => {
          next[String(p.id)] = `${p.name || p.product_name || ""}${p.sku ? ` (${p.sku})` : ""}`;
        });
        return next;
      });
    }
  }, [searchedProductsData]);

  // Product options: by default only show PO items; show server search results only when actively searching
  const productOptions = useMemo(() => {
    const isSearching = Boolean(
      productSearchInput && productSearchInput.trim().length > 0,
    );

    // 1. PO Products
    const filteredPoItems = orderProducts.filter((p) => {
      if (!isSearching) return true;
      const term = productSearchInput.toLowerCase().trim();
      const name = (p.product?.name || p.product?.product_name || "").toLowerCase();
      const sku = (p.product?.sku || "").toLowerCase();
      return name.includes(term) || sku.includes(term);
    });

    const poOptions = filteredPoItems.map((p) => ({
      value: String(p.product_id),
      label: `${p.product?.name || p.product?.product_name || ""}${p.product?.sku ? ` (${p.product.sku})` : ""}`,
      product: p.product,
      isPoItem: true,
      poItem: p,
    }));

    // If NOT searching, only return PO items (clean and uncluttered)
    if (!isSearching) {
      if (poOptions.length > 0) {
        return [
          {
            label: "PO Items",
            options: poOptions,
          },
        ];
      }
      return [];
    }

    // When actively searching:
    const groups = [];

    if (poOptions.length > 0) {
      groups.push({
        label: `PO Items (${poOptions.length})`,
        options: poOptions,
      });
    }

    // Exclude PO items from general search results to avoid duplicate options
    const nonPoProducts = searchedProductsData.filter(
      (prod) =>
        !orderProducts.some((po) => String(po.product_id) === String(prod.id)),
    );

    if (nonPoProducts.length > 0) {
      groups.push({
        label: `Other Products (${nonPoProducts.length})`,
        options: nonPoProducts.map((prod) => ({
          value: String(prod.id),
          label: `${prod.name || prod.product_name || ""}${prod.sku ? ` (${prod.sku})` : ""}`,
          product: prod,
          isPoItem: false,
          poItem: null,
        })),
      });
    }

    return groups;
  }, [orderProducts, searchedProductsData, productSearchInput]);

  // Sync PO reference/notes to GRN if not already set
  useEffect(() => {
    if (!grn.purchase_order_id) return;
    const selectedOrder = orders.find(
      (o) => String(o.id) === String(grn.purchase_order_id),
    );
    if (selectedOrder && !grn.reference) {
      setGrn((prev) => ({
        ...prev,
        reference:
          selectedOrder.tracking_number || selectedOrder.reference || "",
        notes: selectedOrder.internal_notes || selectedOrder.public_notes || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, grn.purchase_order_id]);

  const loadedPoIdRef = useRef(null);
  useEffect(() => {
    if (!grn.purchase_order_id) {
      if (loadedPoIdRef.current !== null) {
        loadedPoIdRef.current = null;
        setGrnItems([]);
      }
      return;
    }

    const selectedOrder = orders.find(
      (o) => String(o.id) === String(grn.purchase_order_id),
    );

    if (selectedOrder && loadedPoIdRef.current !== grn.purchase_order_id) {
      loadedPoIdRef.current = grn.purchase_order_id;
      if (orderProducts.length > 0) {
        const initialItems = orderProducts.map((item) => {
          const qtyToReceive = Math.max(
            0,
            (item.ordered_qty || 0) - (item.received_qty || 0),
          );
          const unitCost = parseFloat(item.unit_cost) || 0;
          const isFoc = item.is_foc || false;

          let discountAmount = 0;
          let taxAmount = 0;
          let totalCost = 0;

          if (isFoc) {
            discountAmount = qtyToReceive * unitCost;
            taxAmount = 0;
            totalCost = 0;
          } else {
            const discType = (item.discount_type || "fixed").toLowerCase();
            const discValue =
              parseFloat(item.discount_value || item.discount) || 0;
            const taxRate = parseFloat(item.tax_percentage) || 0;
            const taxMethod = (item.tax_method || "exclusive").toLowerCase();

            if (discType === "percentage") {
              discountAmount = (qtyToReceive * unitCost * discValue) / 100;
            } else {
              discountAmount = discValue;
            }

            const subtotalAfterDiscount =
              qtyToReceive * unitCost - discountAmount;
            if (taxMethod === "exclusive") {
              taxAmount = subtotalAfterDiscount * (taxRate / 100);
              totalCost = subtotalAfterDiscount + taxAmount;
            } else {
              // Price already includes tax - do not extract; align with sales tax behaviour
              taxAmount = 0;
              totalCost = subtotalAfterDiscount;
            }
          }

          return {
            product_id: item.product_id,
            product: item.product,
            order_quantity: item.ordered_qty,
            received_quantity: qtyToReceive,
            received_quantity_type: "good",
            already_received_quantity: item.received_qty || 0,
            is_foc: isFoc,
            unit_cost: unitCost,
            unit_sell_price: item.unit_sell_price || unitCost,
            unit_sell_price_min: item.unit_sell_price_min || unitCost,
            unit_sell_price_max: item.unit_sell_price_max || unitCost,
            tax_id: item.tax_id || null,
            tax_class: item.tax_class || "",
            tax_percentage: parseFloat(item.tax_percentage) || 0,
            tax_amount: taxAmount,
            tax_method: (item.tax_method || "exclusive").toLowerCase(),
            discount_type: (item.discount_type || "fixed").toLowerCase(),
            discount_value: parseFloat(item.discount_value || item.discount) || 0,
            discount_amount: discountAmount,
            total_cost: totalCost,
            batch_number: "",
            expiry_date: "",
            serial_numbers: [],
            meta: item.meta || [],
            notes: item.notes || item.public_notes || item.internal_notes || "",
          };
        });
        setGrnItems(initialItems);
      } else {
        setGrnItems([]);
      }
    }
  }, [orderProducts, orders, grn.purchase_order_id]);

  // Mutations
  const addGrnMutation = useMutation({
    mutationFn: async (grnData) => {
      const payload = {
        ...grnData,
        purchase_order_id: grn.purchase_order_id,
        store_id: store_id,
        received_by: grn.received_by || null,
        received_date: grn.received_date || null,
        status: grn.status,
        reference: grn.reference || null,
        document: grn.document,
        document_type: grn.document_type,
        document_status: grn.document_status,
        document_url: grn.document_url,
        payment_method: grn.payment_method || null,
        sub_total_amount: parseFloat(grn.sub_total_amount) || 0,
        total_tax: parseFloat(grn.total_tax) || 0,
        total_amount: parseFloat(grn.total_amount) || 0,
        notes: grn.notes,
        meta: grn.meta || [],
        items: grnItems.map((item) => ({
          product_id: item.product_id,
          store_id: store_id,
          order_quantity: parseInt(item.order_quantity) || 0,
          received_quantity: parseInt(item.received_quantity) || 0,
          received_quantity_type: item.received_quantity_type || "good",
          already_received_quantity:
            parseInt(item.already_received_quantity) || 0,
          remaining_quantity: Math.max(
            0,
            (parseInt(item.order_quantity) || 0) -
            ((parseInt(item.already_received_quantity) || 0) +
              (parseInt(item.received_quantity) || 0)),
          ),
          unit_cost: parseFloat(item.unit_cost) || 0,
          unit_sell_price:
            parseFloat(item.unit_sell_price || item.unit_cost) || 0,
          unit_sell_price_min:
            parseFloat(item.unit_sell_price_min || item.unit_cost) || 0,
          unit_sell_price_max:
            parseFloat(item.unit_sell_price_max || item.unit_cost) || 0,
          tax_class: item.tax_class || "",
          tax_percentage: parseFloat(item.tax_percentage || 0),
          tax_amount: parseFloat(item.tax_amount) || 0,
          tax_method: (item.tax_method || "exclusive").toLowerCase(),
          discount_type: (item.discount_type || "fixed").toLowerCase(),
          discount_value: parseFloat(item.discount_value) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          is_foc: item.is_foc || false,
          total_cost: parseFloat(item.total_cost) || 0,
          batch_number: item.batch_number || null,
          expiry_date: item.expiry_date || null,
          serial_numbers: item.serial_numbers || [],
          meta: item.meta || [],
        })),
      };

      const response = await apiClient.post(API_ENDPOINTS.PURCHASES.GRN, payload);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["grns"] });
      toast.success("GRN Added Successfully");
      setGrn((prev) => ({ ...prev, id: data.id }));
      // Generate PDF logic could be triggered here or user can do it from list
      // For newPurchase we just saved. User might want to print PDF immediately.
      generatePDF(data);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add GRN");
    },
  });

  // PDF Generation
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const generatePDF = async (grnData) => {
    toast.info("Generating PDF...");
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.GRN_REPORT(grnData.id),
        {
          headers: { Accept: "application/pdf" },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowSuccessModal(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate PDF");
    }
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `GRN-${grn.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded");
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate(all_routes.editGrn.replace(":id", grn.id));
  };

  const validateFields = () => {
    if (!grn.purchase_order_id) {
      toast.error("Please select a Purchase Order.");
      return false;
    }
    if (grnItems.length === 0) {
      toast.error("Please add at least one item.");
      return false;
    }
    for (let i = 0; i < grnItems.length; i++) {
      const it = grnItems[i];
      const prod =
        it.product ||
        searchedProductsData.find(
          (p) => String(p.id) === String(it.product_id),
        );
      const isSerial =
        prod?.tracking_type === "serial" || prod?.is_serialized;
      const recQty = parseFloat(it.received_quantity);
      if (!isNaN(recQty) && recQty < 0) {
        toast.error(`Received quantity cannot be negative for "${prod?.name || prod?.product_name || `Item ${i + 1}`}".`);
        return false;
      }
      const recQtyInt = parseInt(it.received_quantity) || 0;
      if (isSerial && recQtyInt > 0) {
        const serialsCount = (it.serial_numbers || []).length;
        if (serialsCount !== recQtyInt) {
          toast.error(
            `Serial count (${serialsCount}) must match received quantity (${recQtyInt}) for "${prod?.name || prod?.product_name || `Item ${i + 1}`}".`,
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (addGrnMutation.isPending || addGrnMutation.isSuccess) {
      return;
    }
    if (!validateFields()) {
      return;
    }
    const grnData = { ...grn, store_id };
    addGrnMutation.mutate(grnData);
  };

  const handleGrnChange = (key, value) => {
    setGrn({ ...grn, [key]: value });
  };

  const calculateTotalAmount = (items = grnItems) => {
    let subtotal = 0;
    let totalTax = 0;
    let grandTotal = 0;

    items.forEach((item) => {
      const qty = parseFloat(item.received_quantity) || 0;
      const cost = parseFloat(item.unit_cost) || 0;
      const taxAmount = parseFloat(item.tax_amount) || 0;
      const discountAmount = parseFloat(item.discount_amount) || 0;
      const isFoc = item.is_foc || false;

      // FOC items are free - exclude their cost from subtotal (not a discount)
      if (!isFoc) {
        subtotal += qty * cost - discountAmount;
        totalTax += taxAmount;
      }
      grandTotal += parseFloat(item.total_cost) || 0;
    });

    setGrn((prev) => ({
      ...prev,
      sub_total_amount: subtotal,
      total_tax: totalTax,
      total_amount: grandTotal,
    }));
  };

  useEffect(() => {
    calculateTotalAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnItems]);

  const grnItemChange = (index, key, value, extraProduct) => {
    const newItems = [...grnItems];

    // Ensure quantity fields cannot go negative
    if (key === "received_quantity" || key === "order_quantity") {
      if (value === "" || value === null || value === undefined) {
        newItems[index][key] = "";
      } else {
        const parsed = parseFloat(value);
        newItems[index][key] = isNaN(parsed) || parsed < 0 ? 0 : parsed;
      }
    } else {
      newItems[index][key] = value;
    }

    // Auto-update quantity for serial products
    if (key === "serial_numbers") {
      newItems[index].received_quantity = Math.max(0, (value || []).length);
    }

    // Map PO field data or general product field data when product is selected
    if (key === "product_id" && value) {
      const selectedPOItem = orderProducts.find(
        (p) => String(p.product_id) === String(value),
      );
      if (selectedPOItem) {
        const qtyToReceive = Math.max(
          0,
          (selectedPOItem.ordered_qty || 0) -
          (selectedPOItem.received_qty || 0),
        );
        newItems[index].product = selectedPOItem.product;
        newItems[index].unit_cost = parseFloat(selectedPOItem.unit_cost) || 0;
        newItems[index].unit_sell_price =
          selectedPOItem.unit_sell_price || newItems[index].unit_cost;
        newItems[index].unit_sell_price_min =
          selectedPOItem.unit_sell_price_min || newItems[index].unit_cost;
        newItems[index].unit_sell_price_max =
          selectedPOItem.unit_sell_price_max || newItems[index].unit_cost;
        newItems[index].tax_rate = parseFloat(
          selectedPOItem.tax_percentage || 0,
        );
        newItems[index].tax_percentage = newItems[index].tax_rate;
        newItems[index].tax_method = (
          selectedPOItem.tax_method || "exclusive"
        ).toLowerCase();
        newItems[index].tax_id = selectedPOItem.tax_id || null;
        newItems[index].tax_class = selectedPOItem.tax_class || "";
        newItems[index].order_quantity = selectedPOItem.ordered_qty || 0;
        newItems[index].already_received_quantity =
          selectedPOItem.received_qty || 0;
        newItems[index].received_quantity = qtyToReceive;
        newItems[index].is_foc = selectedPOItem.is_foc || false;
        newItems[index].discount_type = (
          selectedPOItem.discount_type || "fixed"
        ).toLowerCase();
        newItems[index].discount_value =
          parseFloat(
            selectedPOItem.discount_value || selectedPOItem.discount,
          ) || 0;
        newItems[index].notes =
          selectedPOItem.notes ||
          selectedPOItem.public_notes ||
          selectedPOItem.internal_notes ||
          "";
        newItems[index].meta = selectedPOItem.meta || [];
      } else {
        // Non-PO product: fetch info from product object or searched data
        const prod =
          extraProduct ||
          newItems[index].product ||
          searchedProductsData.find((p) => String(p.id) === String(value));

        const cost = parseFloat(
          prod?.cost_price || prod?.cost || prod?.unit_cost || 0,
        );
        const sellPrice = parseFloat(
          prod?.selling_price || prod?.price || prod?.unit_sell_price || cost,
        );

        newItems[index].product = prod;
        newItems[index].order_quantity = 0;
        newItems[index].already_received_quantity = 0;
        const isSerial =
          prod?.tracking_type === "serial" || prod?.is_serialized;
        if (
          !isSerial &&
          (!newItems[index].received_quantity ||
            parseFloat(newItems[index].received_quantity) <= 0)
        ) {
          newItems[index].received_quantity = 1;
        }
        newItems[index].unit_cost = cost;
        newItems[index].unit_sell_price = sellPrice;
        newItems[index].unit_sell_price_min = parseFloat(
          prod?.selling_price_min || prod?.min_price || cost,
        );
        newItems[index].unit_sell_price_max = parseFloat(
          prod?.selling_price_max || prod?.max_price || sellPrice,
        );
        const taxPct = parseFloat(
          prod?.tax?.rate || prod?.tax_percentage || prod?.tax_rate || 0,
        );
        newItems[index].tax_rate = taxPct;
        newItems[index].tax_percentage = taxPct;
        newItems[index].tax_method = (
          prod?.tax_method || "exclusive"
        ).toLowerCase();
        newItems[index].tax_id = prod?.tax_id || null;
        newItems[index].tax_class = prod?.tax_class || "";
        newItems[index].is_foc = false;
        newItems[index].discount_type = "fixed";
        newItems[index].discount_value = 0;
        newItems[index].notes = "";
        newItems[index].meta = [];
      }
    } else if (key === "product_id" && !value) {
      newItems[index].product = null;
      newItems[index].order_quantity = 0;
      newItems[index].already_received_quantity = 0;
      newItems[index].received_quantity = 0;
      newItems[index].unit_cost = 0;
      newItems[index].unit_sell_price = 0;
      newItems[index].tax_percentage = 0;
      newItems[index].discount_value = 0;
      newItems[index].serial_numbers = [];
    }

    // Recalculate line total if any relevant field changes
    if (
      [
        "received_quantity",
        "unit_cost",
        "tax_percentage",
        "tax_method",
        "discount_type",
        "discount_value",
        "is_foc",
        "product_id",
        "serial_numbers",
      ].includes(key)
    ) {
      const qty = parseFloat(newItems[index].received_quantity) || 0;
      const cost = parseFloat(newItems[index].unit_cost) || 0;
      const isFoc = newItems[index].is_foc || false;

      let discountAmount = 0;
      let taxAmount = 0;
      let lineTotal = 0;

      if (isFoc) {
        discountAmount = qty * cost;
        taxAmount = 0;
        lineTotal = 0;
      } else {
        const discType = (
          newItems[index].discount_type || "fixed"
        ).toLowerCase();
        const discValue = parseFloat(newItems[index].discount_value) || 0;
        const taxRate = parseFloat(newItems[index].tax_percentage || 0);
        const taxMethod = (
          newItems[index].tax_method || "exclusive"
        ).toLowerCase();

        if (discType === "percentage") {
          discountAmount = (qty * cost * discValue) / 100;
        } else {
          discountAmount = discValue;
        }

        const subtotalAfterDiscount = qty * cost - discountAmount;
        if (taxMethod === "exclusive") {
          taxAmount = subtotalAfterDiscount * (taxRate / 100);
          lineTotal = subtotalAfterDiscount + taxAmount;
        } else {
          // Price already includes tax - do not extract; align with sales tax behaviour
          taxAmount = 0;
          lineTotal = subtotalAfterDiscount;
        }
      }

      newItems[index].discount_amount = discountAmount;
      newItems[index].tax_amount = taxAmount;
      newItems[index].total_cost = lineTotal;
    }

    // Sync unit sell prices if cost changes and they are unset
    if (key === "unit_cost" && !newItems[index].unit_sell_price) {
      newItems[index].unit_sell_price = newItems[index].unit_cost;
      newItems[index].unit_sell_price_min = newItems[index].unit_cost;
      newItems[index].unit_sell_price_max = newItems[index].unit_cost;
    }

    setGrnItems(newItems);
  };

  const addNewItem = () => {
    if (grnItems.length >= 25) {
      toast.error("You cannot add more than 25 items.");
      return;
    }
    setGrnItems([
      ...grnItems,
      {
        product_id: "",
        order_quantity: 0,
        received_quantity: 0,
        received_quantity_type: "good",
        already_received_quantity: 0,
        unit_cost: 0,
        unit_sell_price: 0,
        unit_sell_price_min: 0,
        unit_sell_price_max: 0,
        tax_id: null,
        tax_percentage: 0,
        tax_amount: 0,
        tax_method: "Exclusive",
        tax_class: "",
        discount_type: "Fixed",
        discount_value: 0,
        discount_amount: 0,
        is_foc: false,
        total_cost: 0,
        batch_number: "",
        expiry_date: "",
        serial_numbers: [],
        notes: "",
        meta: [],
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = [...grnItems];
    newItems.splice(index, 1);
    setGrnItems(newItems);
  };

  const GRNItemConfigModal = () => {
    if (configModal.itemIndex === null) return null;
    const item = grnItems[configModal.itemIndex];
    if (!item) return null;

    const updateField = (key, value) => {
      grnItemChange(configModal.itemIndex, key, value);
    };

    return (
      <Dialog
        open={configModal.isOpen}
        onOpenChange={(open) =>
          setConfigModal((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Item Configuration:{" "}
              {item.product?.name || item.product?.product_name || "Product"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4 border-y">
            <div className="space-y-2">
              <Label>Quantity Type</Label>
              <ReactSelect
                options={[
                  { value: "good", label: "Good" },
                  { value: "damaged", label: "Damaged" },
                  { value: "lost", label: "Lost" },
                  { value: "returned", label: "Returned" },
                  { value: "exchanged", label: "Exchanged" },
                  { value: "other", label: "Other" },
                ]}
                value={{
                  value: item.received_quantity_type,
                  label:
                    item.received_quantity_type.charAt(0).toUpperCase() +
                    item.received_quantity_type.slice(1),
                }}
                onChange={(opt) =>
                  updateField(
                    "received_quantity_type",
                    opt ? opt.value : "good",
                  )
                }
                menuPortalTarget={document.body}
              />
            </div>
            <div className="space-y-4 pt-8">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={item.is_foc}
                  onCheckedChange={(checked) => updateField("is_foc", checked)}
                />
                <Label>Free of Charge (FOC)</Label>
              </div>
            </div>

            <div className="space-y-2 border-t pt-4 mt-2 col-span-2">
              <Label className="text-primary font-bold">Selling Prices</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Sell Price ({currencySymbol})</Label>
                  <Input
                    type="number"
                    value={item.unit_sell_price_min}
                    onChange={(e) =>
                      updateField("unit_sell_price_min", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Sell Price ({currencySymbol})</Label>
                  <Input
                    type="number"
                    value={item.unit_sell_price_max}
                    onChange={(e) =>
                      updateField("unit_sell_price_max", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t pt-4 mt-2 col-span-2">
              <Label className="text-primary font-bold">Discounts</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Type</Label>
                  <ReactSelect
                    options={[
                      { value: "Fixed", label: "Fixed Amount" },
                      { value: "Percentage", label: "Percentage %" },
                    ]}
                    value={{
                      value: item.discount_type,
                      label:
                        item.discount_type === "Percentage"
                          ? "Percentage %"
                          : "Fixed Amount",
                    }}
                    onChange={(opt) =>
                      updateField("discount_type", opt ? opt.value : "Fixed")
                    }
                    isDisabled={item.is_foc}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discount Value</Label>
                  <Input
                    type="number"
                    value={item.discount_value}
                    onChange={(e) =>
                      updateField("discount_value", e.target.value)
                    }
                    disabled={item.is_foc}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t pt-4 mt-2 col-span-2">
              <Label className="text-primary font-bold">Additional Info</Label>
              {item.product?.tracking_type === "standard" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Batch Number</Label>
                    <Input
                      value={item.batch_number}
                      onChange={(e) =>
                        updateField("batch_number", e.target.value)
                      }
                      placeholder="Enter Batch #"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={item.expiry_date}
                      onChange={(e) =>
                        updateField("expiry_date", e.target.value)
                      }
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2 mt-2">
                <Label>Internal Notes</Label>
                <Textarea
                  value={item.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Notes for this item..."
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() =>
                setConfigModal((prev) => ({ ...prev, isOpen: false }))
              }
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // When PO changes, should we clear items?
  // Original didn't explicitly clear but users would see different product list.
  // I'll keep items but they might show "Unknown" product if not in new PO.
  // Better to suggest clearing or just let user handle it.

  return (
    <div className="page-wrapper">
      <div className="content p-6 max-w-[100vw] overflow-x-hidden">
        <PageHeader>
          <PageHeaderHeading
            title="Add New GRN"
            subtitle="Create Goods Received Note"
          />
          <PageHeaderActions>
            <Button variant="outline" onClick={() => navigate(all_routes.grn)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            {hasPermission("goods received note create") && (
              <Button
                onClick={handleSubmit}
                disabled={addGrnMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {addGrnMutation.isPending ? "Saving..." : "Save GRN"}
              </Button>
            )}
          </PageHeaderActions>
        </PageHeader>

        <div className="space-y-6">
          {/* Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>GRN Information</CardTitle>
              <CardDescription>
                Enter details for the Goods Received Note.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Purchase Order */}
                <div className="space-y-2">
                  <Label>
                    Purchase Order <span className="text-red-500">*</span>
                  </Label>
                  <ReactSelect
                    options={orders.map((order) => ({
                      value: String(order.id),
                      label: order.tracking_number,
                    }))}
                    value={
                      grn.purchase_order_id
                        ? orders
                          .map((o) => ({
                            value: String(o.id),
                            label: o.tracking_number,
                          }))
                          .find(
                            (opt) =>
                              opt.value === String(grn.purchase_order_id),
                          ) ||
                        { value: String(grn.purchase_order_id), label: String(grn.purchase_order_id) }
                        : null
                    }
                    onInputChange={(value) => setPoSearchInput(value)}
                    filterOption={() => true}
                    isLoading={isOrdersFetching}
                    onChange={(opt) =>
                      handleGrnChange("purchase_order_id", opt ? opt.value : "")
                    }
                    placeholder="Search Purchase Order..."
                  />
                </div>

                {/* Received By */}
                <div className="space-y-2">
                  <Label>
                    Received By <span className="text-red-500">*</span>
                  </Label>
                  <ReactSelect
                    onInputChange={(value) => setEmployeeSearchInput(value)}
                    isLoading={isEmployeesFetching}
                    filterOption={() => true}
                    options={employees.map((emp) => ({
                      value: String(emp.id),
                      label: emp.name || emp.full_name,
                    }))}
                    value={
                      grn.received_by
                        ? {
                          value: String(grn.received_by),
                          label:
                            employeeLabels[String(grn.received_by)] ||
                            employees
                              .map((emp) => ({
                                value: String(emp.id),
                                label: emp.name || emp.full_name,
                              }))
                              .find(
                                (opt) =>
                                  opt.value === String(grn.received_by),
                              )?.label ||
                            "Select Employee",
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleGrnChange("received_by", opt ? opt.value : "")
                    }
                    placeholder="Select Employee"
                  />
                </div>

                {/* Received Date */}
                <div className="space-y-2">
                  <Label>Received Date</Label>
                  <Input
                    type="date"
                    value={grn.received_date}
                    onChange={(e) =>
                      handleGrnChange("received_date", e.target.value)
                    }
                  />
                </div>

                {/* Reference */}
                <div className="space-y-2">
                  <Label>Reference</Label>
                  <Input
                    value={grn.reference}
                    onChange={(e) =>
                      handleGrnChange("reference", e.target.value)
                    }
                    placeholder="e.g. PO-REF-001"
                    maxLength={255}
                  />
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <Label>GRN Status</Label>
                  <ReactSelect
                    options={[
                      { value: "pending", label: "Pending" },
                      { value: "completed", label: "Completed" },
                      { value: "cancelled", label: "Cancelled" },
                    ]}
                    value={[
                      { value: "pending", label: "Pending" },
                      { value: "completed", label: "Completed" },
                      { value: "cancelled", label: "Cancelled" },
                    ].find((opt) => opt.value === grn.status)}
                    onChange={(opt) =>
                      handleGrnChange("status", opt ? opt.value : "pending")
                    }
                  />
                </div>

                {/* Document */}
                <div className="space-y-2">
                  <Label>Doc Number</Label>
                  <Input
                    value={grn.document}
                    onChange={(e) =>
                      handleGrnChange("document", e.target.value)
                    }
                    placeholder="e.g. INV-123"
                    maxLength={255}
                  />
                </div>

                {/* Payment Method */}
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <ReactSelect
                    options={availablePaymentMethods.map((pm) => ({
                      value: pm.slug || pm.id || pm.name,
                      label: pm.name,
                    }))}
                    value={
                      grn.payment_method
                        ? {
                          value: grn.payment_method,
                          label:
                            availablePaymentMethods.find(
                              (pm) => pm.id === grn.payment_method || pm.slug === grn.payment_method || pm.name === grn.payment_method
                            )?.name || "Select Payment Method",
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleGrnChange("payment_method", opt ? opt.value : "")
                    }
                    isLoading={paymentMethodsLoading}
                    placeholder="Select Payment Method"
                    isClearable
                  />
                </div>

                {/* Document Type */}
                <div className="space-y-2">
                  <Label>Doc Type</Label>
                  <ReactSelect
                    options={[
                      { value: "Invoice", label: "Invoice" },
                      { value: "Delivery Note", label: "Delivery Note" },
                      { value: "Packing List", label: "Packing List" },
                      { value: "Other", label: "Other" },
                    ]}
                    value={
                      grn.document_type
                        ? {
                          value: grn.document_type,
                          label: grn.document_type,
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleGrnChange("document_type", opt ? opt.value : "")
                    }
                  />
                </div>

                {/* Document Status */}
                <div className="space-y-2">
                  <Label>Doc Status</Label>
                  <ReactSelect
                    options={[
                      { value: "Original", label: "Original" },
                      { value: "Copy", label: "Copy" },
                      { value: "Electronic", label: "Electronic" },
                    ]}
                    value={[
                      { value: "Original", label: "Original" },
                      { value: "Copy", label: "Copy" },
                      { value: "Electronic", label: "Electronic" },
                    ].find((opt) => opt.value === grn.document_status)}
                    onChange={(opt) =>
                      handleGrnChange("document_status", opt ? opt.value : "")
                    }
                  />
                </div>
              </div>

              <div className="mt-6">
                <Label>Notes</Label>
                <Textarea
                  value={grn.notes}
                  onChange={(e) => handleGrnChange("notes", e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* GRN Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-muted-foreground" />
                <CardTitle>GRN Items</CardTitle>
              </div>
              {hasPermission("goods received note item create") && (
                <Button onClick={addNewItem} size="sm" type="button" disabled={grnItems.length >= 25}>
                  <Plus className="mr-2 h-4 w-4" /> Add Item
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead className="min-w-[200px]">Product</TableHead>
                      <TableHead className="w-[80px]">Order</TableHead>
                      <TableHead className="w-[80px]">Recv</TableHead>
                      <TableHead className="w-[100px]">Condition</TableHead>
                      <TableHead className="w-[150px]">Tracking Info</TableHead>
                      <TableHead className="w-[110px]">
                        Cost ({currencySymbol})
                      </TableHead>
                      <TableHead className="w-[130px]">Adjustment</TableHead>
                      <TableHead className="w-[120px]">
                        Line Total ({currencySymbol})
                      </TableHead>
                      <TableHead className="w-[80px] text-center">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grnItems.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="text-center py-6 text-muted-foreground"
                        >
                          No items added.
                        </TableCell>
                      </TableRow>
                    ) : (
                      grnItems.map((item, index) => {
                        const isPoItem = orderProducts.some(
                          (p) =>
                            String(p.product_id) === String(item.product_id),
                        );
                        const trackingType =
                          item.product?.tracking_type ||
                          (item.product?.is_serialized ? "serial" : "none");

                        return (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <ReactSelect
                                  options={productOptions}
                                  isLoading={isProductsFetching}
                                  onInputChange={(val, { action }) => {
                                    if (action === "input-change") {
                                      setProductSearchInput(val);
                                    } else if (
                                      action === "menu-close" ||
                                      action === "set-value"
                                    ) {
                                      setProductSearchInput("");
                                    }
                                  }}
                                  filterOption={() => true}
                                  value={(() => {
                                    if (!item.product_id) return null;
                                    const p =
                                      item.product ||
                                      orderProducts.find(
                                        (op) =>
                                          String(op.product_id) ===
                                          String(item.product_id),
                                      )?.product ||
                                      searchedProductsData.find(
                                        (sp) =>
                                          String(sp.id) ===
                                          String(item.product_id),
                                      );
                                    const label = p
                                      ? `${p.name || p.product_name || "Product"}${p.sku ? ` (${p.sku})` : ""}`
                                      : productLabels[String(item.product_id)] ||
                                      "Select Product";
                                    return {
                                      value: String(item.product_id),
                                      label,
                                    };
                                  })()}
                                  onChange={(opt) => {
                                    grnItemChange(
                                      index,
                                      "product_id",
                                      opt ? opt.value : "",
                                      opt?.product,
                                    );
                                    if (opt) {
                                      setProductLabels((prev) => ({
                                        ...prev,
                                        [opt.value]: opt.label,
                                      }));
                                    }
                                  }}
                                  formatOptionLabel={(opt, { context }) => {
                                    if (context === "value") {
                                      return opt.label;
                                    }
                                    return (
                                      <div className="flex items-center justify-between w-full py-0.5 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <span className="font-medium text-sm truncate">
                                            {opt.product?.name ||
                                              opt.product?.product_name ||
                                              opt.label}
                                          </span>
                                          {opt.product?.sku && (
                                            <span className="text-[11px] text-muted-foreground font-mono bg-muted/80 px-1.5 py-0.5 rounded shrink-0">
                                              {opt.product.sku}
                                            </span>
                                          )}
                                        </div>
                                        {opt.isPoItem ? (
                                          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
                                            PO Qty: {opt.poItem?.ordered_qty ?? 0}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                                            Inventory
                                          </span>
                                        )}
                                      </div>
                                    );
                                  }}
                                  noOptionsMessage={() =>
                                    productSearchInput?.trim()
                                      ? "No matching products found"
                                      : "Type to search all inventory products..."
                                  }
                                  placeholder="Search product name or SKU..."
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                  isClearable
                                />
                                <div className="flex flex-wrap items-center gap-1">
                                  {item.product_id && !isPoItem && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-purple-50 text-purple-700 border-purple-200"
                                    >
                                      Non-PO Item
                                    </Badge>
                                  )}
                                  {trackingType === "serial" && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-blue-50/50 text-blue-600 border-blue-200"
                                    >
                                      <ScanBarcode className="h-3 w-3 mr-1" />
                                      Serial Tracking
                                    </Badge>
                                  )}
                                  {trackingType === "standard" && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"
                                    >
                                      <Package className="h-3 w-3 mr-1" />
                                      Batch Tracking
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Input
                                readOnly
                                value={item.order_quantity ?? 0}
                                className="h-9 bg-muted"
                                title={
                                  isPoItem
                                    ? "Ordered quantity in PO"
                                    : "Not ordered in PO (0)"
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.received_quantity}
                                min={0}
                                onKeyDown={(e) => {
                                  if (e.key === "-" || e.key === "e" || e.key === "E") {
                                    e.preventDefault();
                                  }
                                }}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    grnItemChange(index, "received_quantity", "");
                                  } else {
                                    const val = Math.max(0, parseFloat(raw) || 0);
                                    grnItemChange(index, "received_quantity", val);
                                  }
                                }}
                                className="h-9"
                              />
                            </TableCell>
                            <TableCell>
                              <ReactSelect
                                options={[
                                  { value: "good", label: "Good" },
                                  { value: "damaged", label: "Damaged" },
                                  { value: "short", label: "Short" },
                                  { value: "expired", label: "Expired" },
                                ]}
                                value={{
                                  value: item.received_quantity_type || "good",
                                  label:
                                    (item.received_quantity_type || "Good")
                                      .charAt(0)
                                      .toUpperCase() +
                                    (
                                      item.received_quantity_type || "good"
                                    ).slice(1),
                                }}
                                onChange={(opt) =>
                                  grnItemChange(
                                    index,
                                    "received_quantity_type",
                                    opt.value,
                                  )
                                }
                                menuPortalTarget={document.body}
                              />
                            </TableCell>

                            <TableCell>
                              {trackingType === "standard" && (
                                <div className="space-y-1">
                                  <Input
                                    placeholder="Batch #"
                                    value={item.batch_number || ""}
                                    onChange={(e) =>
                                      grnItemChange(
                                        index,
                                        "batch_number",
                                        e.target.value,
                                      )
                                    }
                                    className="h-8 text-xs"
                                  />
                                  <Input
                                    type="date"
                                    value={
                                      item.expiry_date
                                        ? item.expiry_date.split("T")[0]
                                        : ""
                                    }
                                    onChange={(e) =>
                                      grnItemChange(
                                        index,
                                        "expiry_date",
                                        e.target.value,
                                      )
                                    }
                                    className="h-8 text-xs"
                                  />
                                </div>
                              )}
                              {trackingType === "serial" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openSerialModal(index)}
                                  className={`w-full h-8 text-[10px] gap-1 px-1 ${(item.serial_numbers?.length || 0) ===
                                    parseInt(item.received_quantity || 0) &&
                                    parseInt(item.received_quantity || 0) > 0
                                    ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                                    : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                    }`}
                                >
                                  <ScanBarcode className="h-3 w-3" />
                                  {item.serial_numbers?.length || 0} /{" "}
                                  {item.received_quantity || 0} Serials
                                </Button>
                              )}
                              {trackingType === "none" && (
                                <span className="text-xs text-muted-foreground italic text-center block">
                                  No tracking
                                </span>
                              )}
                            </TableCell>

                            <TableCell>
                              <div className="space-y-1">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.unit_cost}
                                  onChange={(e) =>
                                    grnItemChange(
                                      index,
                                      "unit_cost",
                                      e.target.value,
                                    )
                                  }
                                  className={`h-9 ${item.is_foc ? "bg-muted italic" : ""
                                    }`}
                                  readOnly={item.is_foc}
                                />
                                {item.is_foc && (
                                  <Badge className="w-full justify-center bg-green-50 text-green-700 border-green-200 text-[9px] h-4">
                                    FREE OF CHARGE
                                  </Badge>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex flex-wrap gap-1">
                                  {item.is_foc ? (
                                    <span className="text-[10px] text-green-600 font-medium bg-green-50 px-1 rounded">
                                      -100% (FOC)
                                    </span>
                                  ) : (
                                    <>
                                      {parseFloat(item.discount_amount || 0) >
                                        0 && (
                                          <span className="text-[10px] text-red-600 font-medium bg-red-50 px-1 rounded">
                                            Disc: -
                                            {parseFloat(
                                              item.discount_amount,
                                            ).toFixed(2)}
                                          </span>
                                        )}
                                      <span className="text-[10px] text-blue-600 font-medium bg-blue-50 px-1 rounded">
                                        Tax:{" "}
                                        {parseFloat(
                                          item.tax_percentage || 0,
                                        ).toFixed(1)}
                                        %
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div className="text-[9px] text-muted-foreground italic truncate">
                                  {item.tax_method} |{" "}
                                  {item.tax_class || "No Class"}
                                </div>
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="space-y-1 text-right">
                                <Input
                                  readOnly
                                  value={parseFloat(
                                    item.total_cost || 0,
                                  ).toFixed(2)}
                                  className="h-9 bg-muted text-right font-medium"
                                />
                                {parseFloat(item.tax_amount || 0) > 0 && (
                                  <div className="text-[10px] text-muted-foreground px-1">
                                    Incl. Tax: +
                                    {parseFloat(item.tax_amount).toFixed(2)}
                                  </div>
                                )}
                                {item.is_foc && (
                                  <div className="text-[10px] text-muted-foreground px-1">
                                    Free of Charge
                                  </div>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                {hasPermission(
                                  "goods received note item update",
                                ) && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                                      onClick={() =>
                                        setConfigModal({
                                          isOpen: true,
                                          itemIndex: index,
                                        })
                                      }
                                      type="button"
                                    >
                                      <Settings className="h-4 w-4" />
                                    </Button>
                                  )}
                                {hasPermission(
                                  "goods received note item delete",
                                ) && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => removeItem(index)}
                                      type="button"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
            {grnItems.length > 0 && (
              <CardFooter className="flex flex-col items-end py-4 border-t gap-2">
                <div className="w-full max-w-xs flex justify-between items-center text-muted-foreground">
                  <span className="text-sm">Subtotal:</span>
                  <span className="text-sm">
                    {currencySymbol}
                    {parseFloat(grn.sub_total_amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="w-full max-w-xs flex justify-between items-center text-muted-foreground">
                  <span className="text-sm">Total Tax:</span>
                  <span className="text-sm">
                    {currencySymbol}
                    {parseFloat(grn.total_tax || 0).toFixed(2)}
                  </span>
                </div>
                <div className="w-full max-w-xs flex justify-between items-center font-bold text-lg pt-2 border-t">
                  <span>Grand Total:</span>
                  <span>
                    {currencySymbol}
                    {parseFloat(grn.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </CardFooter>
            )}
          </Card>
        </div>

        {/* PDF Success Dialog */}
        <Dialog open={showSuccessModal} onOpenChange={handleCloseSuccessModal}>
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>GRN Created & PDF Ready</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center p-4 space-y-4">
              <div className="text-green-600">
                <FileText size={48} />
              </div>
              <p className="text-center text-muted-foreground">
                GRN has been successfully created. You can download or print the
                PDF.
              </p>
              <div className="flex gap-4">
                <Button onClick={handleDownloadPDF}>
                  <Download className="mr-2 h-4 w-4" /> Download PDF
                </Button>
                <Button variant="secondary" onClick={handleCloseSuccessModal}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Item Config Modal */}
        <GRNItemConfigModal />

        {/* Add Serials Modal */}
        <AddSerialsModal
          isOpen={serialModal.isOpen}
          onClose={() => setSerialModal((prev) => ({ ...prev, isOpen: false }))}
          onSave={handleSaveSerials}
          initialSerials={serialModal.initialSerials}
          quantity={serialModal.quantity}
          productName={serialModal.productName}
          excludeSerials={excludeSerials}
        />
      </div>
    </div>
  );
};

export default NewGRN;
